/**
 * AddPersonModal — create a new Person from the Training Review screen.
 *
 * Ports the field set of the old Edit-page PersonModal (prefix, first/last
 * name, suffix, jersey number, team) into the tank-redesign visual system.
 * On Save it calls createPerson → optional addTeamMember → assignPerson on
 * the active face, then notifies the parent to refresh + advance.
 *
 * Pre-fills first/last name from a search query when the user opens it via
 * the "+ Create '<query>'" affordance below the Search Persons field.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import Modal from '../shared/components/ui/Modal';
import { Btn, Eyebrow, Mono } from '../shared/components/redesign';
import {
  createPerson,
  addTeamMember,
  assignPerson,
  searchTeams,
} from '../api';
import styles from './AddPersonModal.module.css';

function splitName(query) {
  const trimmed = (query || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

export default function AddPersonModal({
  isOpen,
  initialQuery = '',
  activeFace,
  onCancel,
  onSaved,
}) {
  const [prefix, setPrefix] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [jersey, setJersey] = useState('');
  const [team, setTeam] = useState(null);
  const [teamQuery, setTeamQuery] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const firstInputRef = useRef(null);

  // Reset form whenever the modal opens, pre-filling from the search query.
  useEffect(() => {
    if (!isOpen) return;
    const { firstName: f, lastName: l } = splitName(initialQuery);
    setPrefix('');
    setFirstName(f);
    setLastName(l);
    setSuffix('');
    setJersey('');
    setTeam(null);
    setTeamQuery('');
    setTeamSuggestions([]);
    setError(null);
    setSaving(false);
    // Focus first field after the modal mounts.
    const t = setTimeout(() => firstInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, initialQuery]);

  // Debounced team search. Suppressed once a team is chosen.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (team) {
      setTeamSuggestions([]);
      return undefined;
    }
    const q = teamQuery.trim();
    if (q.length < 2) {
      setTeamSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const results = await searchTeams(q);
        if (!cancelled) setTeamSuggestions(Array.isArray(results) ? results.slice(0, 8) : []);
      } catch {
        if (!cancelled) setTeamSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [teamQuery, team, isOpen]);

  const displayName = useMemo(
    () => [prefix, firstName, lastName, suffix].map((s) => s.trim()).filter(Boolean).join(' '),
    [prefix, firstName, lastName, suffix],
  );

  const canSave = !!(firstName.trim() || lastName.trim()) && !saving && !!activeFace;

  const handleSubmit = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const jerseyNum = jersey === '' ? null : Number(jersey);
      const newPerson = await createPerson({
        prefix: prefix.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        suffix: suffix.trim(),
        jersey_number: jerseyNum,
        display_name: displayName,
      });
      if (team?.id) {
        await addTeamMember(team.id, newPerson.id, jerseyNum);
      }
      await assignPerson(activeFace.id, newPerson.id, null);
      onSaved?.({ personId: newPerson.id, displayName });
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.response?.data?.error
        || e?.message
        || 'Failed to save person.';
      setError(typeof msg === 'string' ? msg : 'Failed to save person.');
    } finally {
      setSaving(false);
    }
  }, [canSave, prefix, firstName, lastName, suffix, jersey, team, displayName, activeFace, onSaved]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="md">
      <Modal.Header onClose={onCancel}>Add new person</Modal.Header>
      <Modal.Body>
        <div className={styles.body}>
          <section className={styles.section}>
            <Eyebrow>Name</Eyebrow>
            <div className={styles.grid}>
              <label className={styles.field}>
                <Mono className={styles.label}>Prefix</Mono>
                <input
                  type="text"
                  className={styles.input}
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="Coach, Trainer, Referee…"
                  maxLength={64}
                  disabled={saving}
                />
              </label>
              <label className={styles.field}>
                <Mono className={styles.label}>Suffix</Mono>
                <input
                  type="text"
                  className={styles.input}
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                  placeholder="Jr., Sr.…"
                  maxLength={64}
                  disabled={saving}
                />
              </label>
              <label className={styles.field}>
                <Mono className={styles.label}>First name</Mono>
                <input
                  ref={firstInputRef}
                  type="text"
                  className={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={128}
                  disabled={saving}
                />
              </label>
              <label className={styles.field}>
                <Mono className={styles.label}>Last name</Mono>
                <input
                  type="text"
                  className={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={128}
                  disabled={saving}
                />
              </label>
              <label className={`${styles.field} ${styles.fieldHalf}`}>
                <Mono className={styles.label}>Jersey number</Mono>
                <input
                  type="number"
                  className={styles.input}
                  value={jersey}
                  onChange={(e) => setJersey(e.target.value)}
                  min={0}
                  step={1}
                  disabled={saving}
                />
              </label>
            </div>
          </section>

          <section className={styles.section}>
            <Eyebrow>Team</Eyebrow>
            <div className={styles.teamPicker}>
              {team ? (
                <div className={styles.teamSelected}>
                  <span className={styles.teamName}>{team.name}</span>
                  <button
                    type="button"
                    className={styles.teamClear}
                    onClick={() => {
                      setTeam(null);
                      setTeamQuery('');
                    }}
                    title="Clear team"
                    aria-label="Clear team"
                    disabled={saving}
                  >
                    <XMarkIcon style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className={styles.input}
                    value={teamQuery}
                    onChange={(e) => setTeamQuery(e.target.value)}
                    placeholder="Search team (optional)…"
                    disabled={saving}
                  />
                  {teamSuggestions.length > 0 && (
                    <ul className={styles.teamList}>
                      {teamSuggestions.map((t) => (
                        <li key={t.id}>
                          <button
                            type="button"
                            className={styles.teamRow}
                            onClick={() => {
                              setTeam({ id: t.id, name: t.name });
                              setTeamQuery('');
                              setTeamSuggestions([]);
                            }}
                          >
                            <span className={styles.teamRowName}>{t.name}</span>
                            {t.team_type && (
                              <Mono className={styles.teamRowKind}>{t.team_type}</Mono>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </section>

          {displayName && (
            <section className={styles.section}>
              <Eyebrow>Will be saved as</Eyebrow>
              <Mono className={styles.preview}>{displayName}</Mono>
            </section>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.footer}>
          <Btn kind="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Btn>
          <Btn kind="primary" onClick={handleSubmit} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save & assign'}
          </Btn>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
